import { BaseService } from './base'
import { APIPath } from 'utility/constant'
import { CommonUtility } from 'utility/common'

class Inspection {
    copyInspection(reqData) {
        const data = CommonUtility.objectToParams(reqData)
        return BaseService.post(`${APIPath.copyInspection}?${data}`)
    }

    getAudit(id) {
        return BaseService.get(`${APIPath.getAudit}${id}`)
    }

    removeInspection(auditTemplateId) {
        const removeUrl = `${APIPath.inspection}/${auditTemplateId}/Remove`
        return BaseService.postExact(removeUrl, undefined).catch(() =>
            BaseService.post(removeUrl, undefined).catch(() =>
                BaseService.remove(removeUrl),
            ),
        )
    }

    saveInspection(data) {
        return BaseService.post(`${APIPath.inspection}`, data)
    }

    updateGroup(data) {
        return BaseService.post(`${APIPath.teamGroup}`, data)
    }

    addNotes(data) {
        return BaseService.post(`${APIPath.addNote}`, data)
    }

    updateNote(data) {
        return BaseService.put(`${APIPath.addNote}`, data)
    }

    removeNote(id) {
        return BaseService.remove(`${APIPath.addNote}?NoteId=${id}`)
    }

    updateNotes(data) {
        return BaseService.put(`${APIPath.addNote}`, data)
    }

    getMediaS3URL = data => {
        return BaseService.post(`${APIPath.uploadSignedUrl}`, data)
    }

    saveMedia = data => {
        return BaseService.post(`${APIPath.saveUpload}`, data)
    }

    uploadtoS3 = data => {
        return BaseService.upload(data.url, data.media, data.header)
    }

    uploadMedia = data => {
        const formData = new FormData()
        formData.set('media', data.media)
        const url = `${APIPath.mediaUpload}${
            data.callfrom ? `?callfrom=${data.callfrom}` : ''
        }`
        return BaseService.upload(url, formData, data.header)
    }

    getReport = id => {
        return BaseService.get(`${APIPath.inspectionReport}${id}`)
    }

    getAuditEmails(auditId, { page = 1, pageSize = 50 } = {}) {
        if (auditId == null || auditId === '') {
            return Promise.reject({
                status: 400,
                error: { code: 'INVALID_PAGING', message: 'Missing audit id' },
            })
        }
        const safePage = Math.min(
            100000,
            Math.max(1, Math.trunc(Number(page)) || 1),
        )
        const safePageSize = Math.min(
            200,
            Math.max(1, Math.trunc(Number(pageSize)) || 50),
        )
        return BaseService.get(
            `${APIPath.auditEmails(auditId)}?page=${safePage}&pageSize=${safePageSize}`,
        )
    }

    getInspectionReportWithTickets = auditId => {
        const params = new URLSearchParams({
            AuditId: String(auditId),
            includePhotos: 'true',
        })
        return BaseService.get(
            `${APIPath.inspectionReportWithTickets}?${params.toString()}`,
        )
    }

    getReviewReport = payload => {
        return BaseService.post(APIPath.inspectionReview, payload)
    }

    getCustomeReport = id => {
        return BaseService.get(
            `${APIPath.inspectionCustomReport}${id}&customReportId=2`,
        )
    }

    shareReport(reqData) {
        const data = CommonUtility.objectToParams(reqData)
        return BaseService.post(`${APIPath.shareReport}?${data}`)
    }

    shareReportByEmails(data) {
        return BaseService.post(APIPath.shareReportByEmails, data)
    }

    getEmailRecipients(topN = 20) {
        const safeTop = Math.max(1, Math.trunc(Number(topN)) || 20)
        return BaseService.get(`${APIPath.emailRecipients}?topN=${safeTop}`)
    }

    sendMultiReport(data) {
        return BaseService.post(APIPath.sendMultiReport, data)
    }

    copySection(reqData) {
        const data = CommonUtility.objectToParams(reqData)
        return BaseService.post(`${APIPath.copySection}?${data}`)
    }

    removeSection({ TemplateId, SectionId, LoginSource = 1 }) {
        return BaseService.post(
            APIPath.removeSection,
            { TemplateId, SectionId, LoginSource },
        )
    }

    /**
     * Upsert section N/A selection for an audit.
     * Body: { AuditId, SectionId, IsNASelected }
     * Response: { Success, IsNASelected, ... }
     */
    updateSectionNAState({ AuditId, SectionId, IsNASelected }) {
        return BaseService.post(APIPath.sectionNAState, {
            AuditId,
            SectionId,
            IsNASelected: !!IsNASelected,
        })
    }

    publicInspection(data) {
        console.log('Calling publicInspection API with tokenId:', data.tokenId);

        // Simply pass the tokenId as a query parameter
        const params = CommonUtility.objectToParams({
            tokenId: data.tokenId,
        });

        console.log('API URL:', `${APIPath.publicInspectionTask}?${params}`);
        return BaseService.get(`${APIPath.publicInspectionTask}?${params}`);
    }

    submitPublicInspection(data) {
        console.log('Submitting public inspection data:', data)
        // Use the dedicated submit endpoint
        return BaseService.post(APIPath.publicInspectionSubmit, data)
    }

    completePublicInspection(templateId) {
        console.log('Completing public inspection with templateId:', templateId)
        // Return the complete response so we can show the success message
        return BaseService.post(`${APIPath.publicInspectionComplete}?templateID=${templateId}`)
            .then(response => {
                // Return both success status and message for UI display
                return {
                    success: response.Success,
                    message: response.Message || "Thank you! Your inspection has been successfully completed.",
                };
            });
    }

    questionComment(data) {
        return BaseService.post(`${APIPath.questionComment}`, data)
    }

    sectionComment(data) {
        return BaseService.post(`${APIPath.sectionComment}`, data)
    }

    taskRejection(data) {
        return BaseService.post(`${APIPath.taskRejection}`, data)
    }

    deleteMedia(mediaId) {
        return BaseService.post(`${APIPath.deleteMedia}?mediaId=${mediaId}`)
    }
}

/** Normalize POST Inspection/Preview response for review UI. */
export const parseInspectionPreviewResponse = response => {
    if (!response) {
        return {
            success: false,
            reportPath: null,
            errorMessage: 'No response from preview API.',
            auditId: null,
        }
    }

    const body =
        response?.Data &&
        typeof response.Data === 'object' &&
        !Array.isArray(response.Data)
            ? response.Data
            : response

    const success =
        body.Success === true ||
        body.Success === 1 ||
        String(body.Success).toLowerCase() === 'true'

    return {
        success,
        reportPath: body.ReportPath || body.reportPath || null,
        errorMessage: body.ErrorMessage || body.errorMessage || null,
        auditId: body.AuditId || body.AuditTemplateId || body.Id || null,
    }
}

const InspectionService = new Inspection()
Object.freeze(InspectionService)
export { InspectionService }
